<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Button from "primevue/button";
import Tag from "primevue/tag";
import Dialog from "primevue/dialog";
import InputNumber from "primevue/inputnumber";

import ReviewRow from "../components/ReviewRow.vue";
import ReviewDrawer from "../components/ReviewDrawer.vue";
import EventRow from "../components/EventRow.vue";
import EventDrawer from "../components/EventDrawer.vue";
import EventCreateForm from "../components/EventCreateForm.vue";
import PendingResponseBoard from "../components/PendingResponseBoard.vue";
import { reviewPath } from "../deeplink";
import { defaultPendingMinDays } from "../pendingfilter";
import { relativeAgo, tickIntervalMs } from "../reltime";
import { useToast } from "primevue/usetoast";
import {
  approveReview,
  bulkDeleteReviews,
  fetchPendingResponseBoard,
  fetchReview,
  fetchReviews,
  fetchStandaloneEvents,
  ApiError,
  type Contributor,
  type PendingBoardColumn,
  type QualityEvent,
  type Review,
} from "../api";

const toast = useToast();

const props = defineProps<{ email: string | null; isManager: boolean }>();

// The dashboard splits content under a tab toggle:
//   - "yours"   — personal involvement (non-managers land here; it's their only lens).
//   - "manager" — the manager firehose (managers only; tab is hidden otherwise).
//     Managers default here since the pending-response board is their primary view.
// "Needs your action" lives outside the tabs because it's always personal
// and covers every role you currently hold.
type Tab = "yours" | "manager";
const tab = ref<Tab>(props.isManager ? "manager" : "yours");

// Personal-involvement data (fetched for everyone).
const involvedReviews = ref<Review[]>([]);          // scope=involved
const contributorReviews = ref<Review[]>([]);       // scope=contributor
const involvedEvents = ref<QualityEvent[]>([]);     // standalone, scope=involved

// Manager-view data (only fetched when isManager).
const allReviews = ref<Review[]>([]);                 // scope=all
const allStandaloneEvents = ref<QualityEvent[]>([]);  // standalone, scope=all
const pendingBoard = ref<PendingBoardColumn[]>([]);   // pending-response Kanban
// Board filter: only count a review as pending once it has been approved
// (released to the contributor) for at least this many days. Defaults to 1,
// or 3 on Mondays so a Friday approval isn't treated as overdue after the
// unworked weekend (see pendingfilter.ts). The manager can override it.
const pendingMinDays = ref(defaultPendingMinDays(new Date().getDay()));

// Review fetched directly by id via the deep-link route (/#/reviews/:id),
// kept separate from the scope lists so it doesn't pollute them. The
// `openReview` computed falls back to this when the id isn't in any list —
// e.g. an archived review reached by an old email link.
const directReview = ref<Review | null>(null);

// Why the deep-link fetch failed, when it did: the backend 403s a review the
// caller may not see and 404s one that doesn't exist. Shown in the drawer's
// place so a shared link that lands on someone without access says so,
// instead of silently rendering an empty drawer.
const deepLinkError = ref<string | null>(null);

const initialLoad = ref(true);
const error = ref<string | null>(null);

// Manual refresh: re-run the same fetches the initial load does, without the
// full-screen loading state, so a long-open tab can be brought up to date in
// place (no reload, keeps the current tab / scroll / open drawer). `refreshing`
// drives the button spinner; `lastRefreshed` feeds the freshness stamp.
const refreshing = ref(false);
const lastRefreshed = ref<number | null>(null);

// A ticking clock so the "Updated Xm ago" label ages on its own without a user
// action. The cadence matches the label's granularity (see `tickIntervalMs`):
// every 5 min while it reads in minutes, then hourly, then daily. A
// self-rescheduling timeout (not a fixed interval) lets it step down through
// those cadences as the data ages, and reschedule instantly on a refresh.
const nowTick = ref(Date.now());
let tickTimer: ReturnType<typeof setTimeout> | undefined;

function clearTick(): void {
  if (tickTimer !== undefined) {
    clearTimeout(tickTimer);
    tickTimer = undefined;
  }
}

function scheduleTick(): void {
  clearTick();
  const age = lastRefreshed.value === null ? 0 : Date.now() - lastRefreshed.value;
  tickTimer = setTimeout(() => {
    nowTick.value = Date.now();
    scheduleTick();
  }, tickIntervalMs(age));
}

const refreshedAgo = computed<string>(() =>
  lastRefreshed.value === null ? "" : relativeAgo(lastRefreshed.value, nowTick.value),
);
const lastRefreshedTitle = computed<string>(() =>
  lastRefreshed.value === null ? "" : new Date(lastRefreshed.value).toLocaleString(),
);

// Review drawer state.
const openReviewId = ref<number | null>(null);
const reviewDrawerVisible = ref(false);

// Manager bulk-delete (Needs your action inbox → pending-approval rows).
// `selectedIds` is a reactive Set; it's reassigned (not mutated in place) on
// every change so Vue re-renders.
const selectMode = ref(false);
const selectedIds = ref<Set<number>>(new Set());
const bulkConfirmVisible = ref(false);
const bulkDeleting = ref(false);

// Event drawer state.
const openEventId = ref<number | null>(null);
const eventDrawerVisible = ref(false);

// Submit-event drawer state.
const submitDrawerVisible = ref(false);

// Reviews you're involved in but NOT as a contributor (author / event owner /
// event reporter). REQUIREMENTS keeps the contributor view distinct, so this
// is the "other involvement" bucket beside it.
const otherInvolvedReviews = computed<Review[]>(() => {
  const contribIds = new Set(contributorReviews.value.map((r) => r.id));
  return involvedReviews.value.filter((r) => !contribIds.has(r.id));
});

const openReview = computed<Review | null>(() => {
  if (openReviewId.value === null) return null;
  // Search every list we hold so the drawer works regardless of which tab
  // surfaced the click.
  for (const list of [
    involvedReviews.value,
    contributorReviews.value,
    allReviews.value,
  ]) {
    const found = list.find((r) => r.id === openReviewId.value);
    if (found) return found;
  }
  if (directReview.value?.id === openReviewId.value) return directReview.value;
  return null;
});

const openEvent = computed<QualityEvent | null>(() => {
  if (openEventId.value === null) return null;
  for (const list of [involvedEvents.value, allStandaloneEvents.value]) {
    const found = list.find((e) => e.id === openEventId.value);
    if (found) return found;
  }
  return null;
});

// The shared fetch: pulls the personal set (everyone) and the manager set
// (managers only) and swaps them into the reactive lists. Throws on failure so
// each caller decides how to surface it — full-page error on first load, toast
// on a manual refresh. Stamps `lastRefreshed` only on success.
async function loadDashboard(): Promise<void> {
  const personal = Promise.all([
    fetchReviews("involved"),
    fetchReviews("contributor"),
    fetchStandaloneEvents("involved"),
  ]);
  const managerView = props.isManager
    ? Promise.all([
        fetchReviews("all"),
        fetchStandaloneEvents("all"),
        fetchPendingResponseBoard(pendingMinDays.value),
      ])
    : Promise.resolve([[], [], []] as [
        Review[],
        QualityEvent[],
        PendingBoardColumn[],
      ]);

  const [[involved, contrib, evInv], [all, allEv, board]] = await Promise.all([
    personal,
    managerView,
  ]);
  involvedReviews.value = involved;
  contributorReviews.value = contrib;
  involvedEvents.value = evInv;
  allReviews.value = all;
  allStandaloneEvents.value = allEv;
  pendingBoard.value = board;
  lastRefreshed.value = Date.now();
}

async function initialRefresh(): Promise<void> {
  try {
    await loadDashboard();
  } catch (e) {
    error.value = String(e);
  } finally {
    initialLoad.value = false;
  }
}

// Manual refresh from the header button. Unlike the initial load, a failure
// here keeps the current (stale) content on screen and surfaces the error as a
// toast, rather than replacing everything with the error view.
async function refresh(): Promise<void> {
  if (refreshing.value) return;
  refreshing.value = true;
  try {
    await loadDashboard();
    error.value = null;
    // The lastRefreshed watch snaps the stamp to "just now" and reschedules
    // the tick, so nothing else to do on success here.
  } catch (e) {
    toast.add({
      severity: "error",
      summary: "Refresh failed",
      detail: String(e),
      life: 5000,
    });
  } finally {
    refreshing.value = false;
  }
}

onMounted(() => void initialRefresh());
onUnmounted(clearTick);

// (Re)start the ticking clock whenever the data is loaded or refreshed —
// lastRefreshed flips null→timestamp on first load and updates on each
// refresh. Rescheduling here resets the cadence to the new (younger) age, so a
// refresh of day-old data immediately drops back to the 5-minute tick.
watch(lastRefreshed, () => {
  nowTick.value = Date.now();
  scheduleTick();
});

// --- deep link (/#/reviews/:id) ------------------------------------------
//
// The URL is the single source of truth for "which review is open": opening a
// review navigates, and the watcher below is what actually opens the drawer.
// That makes every review addressable — the address bar always names the
// review you're looking at, so the URL can be copied and shared — and makes
// the browser Back button close the drawer for free.

const route = useRoute();
const router = useRouter();

// React both to a route hit on mount and to in-app navigations (a click on a
// review row, Back, or a deep-link URL pasted into an already-open tab).
watch(
  () => route.params.id,
  async (raw) => {
    const id = typeof raw === "string" ? parseInt(raw, 10) : NaN;
    if (Number.isNaN(id)) {
      // Navigated off a review URL (Back, or the drawer's close button).
      reviewDrawerVisible.value = false;
      openReviewId.value = null;
      deepLinkError.value = null;
      return;
    }
    openReviewId.value = id;
    reviewDrawerVisible.value = true;
    deepLinkError.value = null;
    // Always issue the direct fetch — covers reviews that the scope lists
    // wouldn't return (archived window, or a role mismatch). If the review
    // is also in one of the lists, `openReview`'s list lookup wins and
    // continues to receive in-place updates via `patchReview`.
    try {
      directReview.value = await fetchReview(id);
    } catch (e) {
      // A shared link is only as good as the recipient's access: the backend
      // 403s a review the caller may not see and 404s one that isn't there.
      // Say which, rather than leaving an empty drawer.
      if (directReview.value?.id === id) directReview.value = null;
      deepLinkError.value =
        e instanceof ApiError && e.status === 403
          ? "You don't have access to this review."
          : e instanceof ApiError && e.status === 404
            ? "That review doesn't exist."
            : `Could not load review R-${id}: ${e}`;
    }
  },
  { immediate: true },
);

// Open a review by navigating: the watcher above does the rest. `push` (not
// `replace`) so Back returns to the plain dashboard and closes the drawer.
function navigateToReview(id: number): void {
  if (route.name === "review-deep-link" && route.params.id === String(id)) {
    // Already on this review's URL — re-show the drawer without stacking a
    // duplicate history entry (the watcher won't re-fire for an equal param).
    reviewDrawerVisible.value = true;
    return;
  }
  void router.push(reviewPath(id));
}

// Close by navigating back to the bare dashboard URL. `replace` so closing a
// drawer doesn't leave a "reopen it" entry sitting in the history stack.
function closeReviewDrawer(): void {
  if (route.name === "review-deep-link") {
    void router.replace({ name: "dashboard" });
    return;
  }
  reviewDrawerVisible.value = false;
  openReviewId.value = null;
}

// --- patch-in-place after actions ----------------------------------------

async function patchReview(id: number): Promise<void> {
  try {
    const fresh = await fetchReview(id);
    // Update-in-place across every list that already held this review.
    // Never silently inject a previously-unseen review — visibility of a
    // review is set at initial load.
    patchExisting(involvedReviews, fresh);
    patchExisting(contributorReviews, fresh);
    patchExisting(allReviews, fresh);
    // Keep the deep-link fallback in sync too, in case the drawer is only
    // backed by `directReview` (deep-linked review not in any list).
    if (directReview.value?.id === id) directReview.value = fresh;
  } catch (e) {
    error.value = String(e);
  }
  // A response/approval/edit can change what's pending, so refresh the manager
  // board. Cheap and keeps it from going stale after an in-app action.
  await refreshPendingBoard();
}

async function refreshPendingBoard(): Promise<void> {
  if (!props.isManager) return;
  try {
    pendingBoard.value = await fetchPendingResponseBoard(pendingMinDays.value);
  } catch (e) {
    // Non-fatal: leave the last-known board up rather than blanking the page.
    console.warn("pending-response board refresh failed", e);
  }
}

// Re-fetch whenever the manager changes the pending-duration filter. The
// backend re-derives columns + padding for the new threshold; the board just
// renders what comes back.
watch(pendingMinDays, (v) => {
  if (v === null || v < 0) pendingMinDays.value = 0;
  else void refreshPendingBoard();
});

function patchExisting<T extends { id: number }>(
  list: { value: T[] },
  fresh: T,
): void {
  const idx = list.value.findIndex((r) => r.id === fresh.id);
  if (idx >= 0) {
    list.value.splice(idx, 1, fresh);
  }
}

// --- removal after delete -------------------------------------------------
// Deleted reviews must drop out of every list (patchReview only updates in
// place). Also closes the drawer / clears the deep-link fallback if it was
// showing a now-deleted review.
function removeReviews(ids: number[]): void {
  const gone = new Set(ids);
  for (const list of [involvedReviews, contributorReviews, allReviews]) {
    list.value = list.value.filter((r) => !gone.has(r.id));
  }
  if (directReview.value && gone.has(directReview.value.id)) {
    directReview.value = null;
  }
  if (openReviewId.value !== null && gone.has(openReviewId.value)) {
    // Also drops the /#/reviews/:id URL — the review it names is gone.
    closeReviewDrawer();
  }
  // Reassign (not mutate) so the Set's reactivity fires.
  selectedIds.value = new Set([...selectedIds.value].filter((id) => !gone.has(id)));
}

// The drawer shows its own success toast; here we only update the lists.
function onReviewDeleted(id: number): void {
  removeReviews([id]);
}

async function refreshStandaloneEvents(_eid?: number): Promise<void> {
  // Full refetch (not patch-in-place): this path also fires after a fresh
  // event is created via EventCreateForm, and we WANT newly-created events
  // to appear here without a page reload. Deliberate asymmetry with
  // `patchReview`, which is strictly update-only.
  try {
    const personal = fetchStandaloneEvents("involved");
    const managerView = props.isManager
      ? fetchStandaloneEvents("all")
      : Promise.resolve([] as QualityEvent[]);
    const [inv, all] = await Promise.all([personal, managerView]);
    involvedEvents.value = inv;
    allStandaloneEvents.value = all;
  } catch (e) {
    error.value = String(e);
  }
}

// --- "needs your action" derivation --------------------------------------

interface ApproveItem { kind: "approve"; review: Review; }
interface RespondItem {
  kind: "respond";
  source: "review" | "event";
  review?: Review;
  event: QualityEvent;
  contributor: Contributor;
}
interface FinalizeItem {
  kind: "finalize";
  source: "review" | "event";
  review?: Review;
  event: QualityEvent;
  contributor: Contributor;
}
type ActionItem = ApproveItem | RespondItem | FinalizeItem;

const needsAction = computed<ActionItem[]>(() => {
  if (!props.email) return [];
  const me = props.email;
  const items: ActionItem[] = [];

  if (props.isManager) {
    // Approval is a manager-role action — sourced from the full firehose.
    for (const r of allReviews.value) {
      if (!r.approved_by) items.push({ kind: "approve", review: r });
    }
  }

  // Respond / finalize: derived from the personal-involvement set.
  const seenReviews = new Map<number, Review>();
  for (const r of [...involvedReviews.value, ...contributorReviews.value]) {
    seenReviews.set(r.id, r);
  }
  for (const r of seenReviews.values()) {
    for (const e of r.events) collectEventActions(e, items, me, "review", r);
  }

  for (const e of involvedEvents.value) {
    collectEventActions(e, items, me, "event");
  }

  return items;
});

function collectEventActions(
  e: QualityEvent,
  items: ActionItem[],
  me: string,
  source: "review" | "event",
  review?: Review,
): void {
  for (const c of e.contributors) {
    if (c.contributor === me && c.contributor_decision === null) {
      items.push({ kind: "respond", source, review, event: e, contributor: c });
    }
    if (
      e.owner === me &&
      e.status !== "closed" &&
      c.contributor_decision !== null &&
      c.owner_decision === null
    ) {
      items.push({ kind: "finalize", source, review, event: e, contributor: c });
    }
  }
}

function actionTagLabel(kind: ActionItem["kind"]): string {
  if (kind === "approve") return "Approve";
  if (kind === "respond") return "Respond";
  return "Finalise";
}

function actionTagSeverity(kind: ActionItem["kind"]): "warn" | "info" | "success" {
  if (kind === "approve") return "warn";
  if (kind === "respond") return "info";
  return "success";
}

// Respond / finalise: the action is event-scoped, but the drawer we open
// depends on whether the event is attached to a review (review drawer, for
// full context) or standalone (event drawer). Approve doesn't reach this
// function — that path renders a ReviewRow whose @open already routes to
// the review drawer.
function onActionEventOpen(item: RespondItem | FinalizeItem): void {
  if (item.source === "review" && item.review) {
    navigateToReview(item.review.id);
  } else {
    openEventId.value = item.event.id;
    eventDrawerVisible.value = true;
  }
}

function onOpenReview(id: number): void {
  navigateToReview(id);
}

// One-click approval for a passing review (0 events — nothing to curate).
// Same endpoint and idempotency as the drawer's "Approve now"; we patch the
// review in place so it drops out of "Needs your action".
const approvingId = ref<number | null>(null);
async function onQuickApprove(id: number): Promise<void> {
  approvingId.value = id;
  try {
    await approveReview(id);
    await patchReview(id);
    toast.add({
      severity: "success",
      summary: "Approved",
      detail: `Review R-${id} approved.`,
      life: 3000,
    });
  } catch (e) {
    toast.add({
      severity: "error",
      summary: "Approval failed",
      detail: String(e),
      life: 5000,
    });
  } finally {
    approvingId.value = null;
  }
}
function onOpenEvent(id: number): void {
  openEventId.value = id;
  eventDrawerVisible.value = true;
}

// --- bulk delete (Needs your action → pending-approval rows) --------------

// The deletable rows in the inbox are exactly the approve-kind items (pending
// approval reviews). Events in the inbox are not deletable here, so selection
// is derived from this set rather than from `allReviews`.
const needsActionReviews = computed<Review[]>(() =>
  needsAction.value
    .filter((it): it is ApproveItem => it.kind === "approve")
    .map((it) => it.review),
);
const selectedReviews = computed<Review[]>(() =>
  needsActionReviews.value.filter((r) => selectedIds.value.has(r.id)),
);
const selectedEventCount = computed<number>(() =>
  selectedReviews.value.reduce((n, r) => n + r.events.length, 0),
);

function toggleSelectMode(): void {
  selectMode.value = !selectMode.value;
  if (!selectMode.value) selectedIds.value = new Set();
}

function onToggleSelect(id: number): void {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
}

function openBulkConfirm(): void {
  if (selectedIds.value.size > 0) bulkConfirmVisible.value = true;
}

async function confirmBulkDelete(): Promise<void> {
  const ids = [...selectedIds.value];
  if (!ids.length) return;
  bulkDeleting.value = true;
  try {
    const result = await bulkDeleteReviews(ids);
    removeReviews(ids);
    bulkConfirmVisible.value = false;
    selectMode.value = false;
    toast.add({
      severity: "success",
      summary: "Reviews deleted",
      detail:
        `Deleted ${result.deleted_reviews} ` +
        `review${result.deleted_reviews === 1 ? "" : "s"} and ` +
        `${result.deleted_events} quality ` +
        `event${result.deleted_events === 1 ? "" : "s"}.`,
      life: 4000,
    });
  } catch (e) {
    // All-or-nothing on the backend: on failure nothing was deleted, so just
    // surface the error and leave the selection intact for a retry.
    bulkConfirmVisible.value = false;
    toast.add({
      severity: "error",
      summary: "Bulk delete failed",
      detail: String(e),
      life: 6000,
    });
  } finally {
    bulkDeleting.value = false;
  }
}
</script>

<template>
  <section v-if="initialLoad" class="loading">
    <i class="pi pi-spinner pi-spin"></i> Loading…
  </section>
  <section v-else-if="error" class="error">{{ error }}</section>
  <template v-else>
    <header class="page-intro">
      <div class="intro-text">
        <h1>Hi {{ email?.split("@")[0] }} — here's what's waiting.</h1>
        <p class="muted">
          The Quality Ledger records code &amp; process reviews and any quality
          events that came out of them.
          <template v-if="isManager">
            Switch to <em>Manager view</em> to see everything in the ledger.
          </template>
          <template v-else>
            You see the reviews and events that involve you directly.
          </template>
        </p>
      </div>
      <div class="intro-actions">
        <div class="refresh-group">
          <span
            v-if="lastRefreshed"
            class="refresh-stamp"
            :title="'Last updated ' + lastRefreshedTitle"
            data-testid="refresh-stamp"
          >
            Updated {{ refreshedAgo }}
          </span>
          <Button
            icon="pi pi-refresh"
            severity="secondary"
            text
            rounded
            :loading="refreshing"
            aria-label="Refresh"
            title="Refresh now"
            data-testid="refresh-btn"
            @click="refresh"
          />
        </div>
        <Button
          label="Submit a quality event"
          icon="pi pi-plus"
          data-testid="open-submit-event"
          @click="submitDrawerVisible = true"
        />
      </div>
    </header>

    <!-- Action inbox: always-visible, role-agnostic, sits above the tabs.
         Managers also get bulk-delete here: a Select toggle reveals a checkbox
         on each pending-approval review row (approve items only — event rows
         in the inbox are not deletable). -->
    <section class="card-section">
      <div class="section-head">
        <h2 data-testid="needs-action-heading">Needs your action</h2>
        <Button
          v-if="isManager && needsActionReviews.length"
          :label="selectMode ? 'Done' : 'Select'"
          :icon="selectMode ? 'pi pi-times' : 'pi pi-check-square'"
          severity="secondary"
          size="small"
          text
          data-testid="select-mode-toggle"
          @click="toggleSelectMode"
        />
      </div>

      <!-- Selection action bar — appears once at least one row is ticked. -->
      <div
        v-if="selectMode && selectedIds.size > 0"
        class="select-bar"
        data-testid="bulk-delete-bar"
      >
        <span class="select-count">
          {{ selectedIds.size }} selected
          <span class="muted">· {{ selectedEventCount }} quality
            event{{ selectedEventCount === 1 ? "" : "s" }}</span>
        </span>
        <Button
          :label="'Delete ' + selectedIds.size + ' review' + (selectedIds.size === 1 ? '' : 's')"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          data-testid="bulk-delete-btn"
          @click="openBulkConfirm"
        />
      </div>

      <p v-if="!needsAction.length" data-testid="no-actions" class="muted">
        Nothing waiting on you. Have a cup of tea.
      </p>
      <ul v-else class="action-list" data-testid="action-list">
        <li
          v-for="(item, idx) in needsAction"
          :key="idx"
          :data-action-kind="item.kind"
          class="action-item"
        >
          <Tag
            class="action-tag"
            :severity="actionTagSeverity(item.kind)"
            :value="actionTagLabel(item.kind)"
          />
          <!-- Same row component as the lists below, so the inbox and the
               lists carry identical info (severity, pending-approval badge,
               decision summary). Approve is review-scoped; respond/finalise
               are event-scoped. -->
          <template v-if="item.kind === 'approve'">
            <ReviewRow
              class="action-target"
              :review="item.review"
              :selectable="selectMode"
              :selected="selectedIds.has(item.review.id)"
              @open="onOpenReview"
              @toggle-select="onToggleSelect"
            />
            <!-- Passing review (no events): nothing to curate, so offer a
                 one-click approve right here instead of opening the drawer. -->
            <Button
              v-if="item.review.events.length === 0"
              class="quick-approve"
              label="Approve"
              icon="pi pi-check"
              size="small"
              :loading="approvingId === item.review.id"
              :data-testid="'quick-approve-' + item.review.id"
              @click="onQuickApprove(item.review.id)"
            />
          </template>
          <EventRow
            v-else
            class="action-target"
            :event="item.event"
            @open="onActionEventOpen(item)"
          />
        </li>
      </ul>
    </section>

    <!-- Tab bar (only present for managers; non-managers see only their lens). -->
    <div v-if="isManager" class="tab-bar" data-testid="tab-bar" role="tablist">
      <button
        type="button"
        :class="['tab-btn', { active: tab === 'yours' }]"
        role="tab"
        :aria-selected="tab === 'yours'"
        data-testid="tab-yours"
        @click="tab = 'yours'"
      >
        Yours
      </button>
      <button
        type="button"
        :class="['tab-btn', { active: tab === 'manager' }]"
        role="tab"
        :aria-selected="tab === 'manager'"
        data-testid="tab-manager"
        @click="tab = 'manager'"
      >
        Manager view
      </button>
    </div>

    <!-- "Yours" tab — everyone sees this lens. -->
    <template v-if="tab === 'yours'">
      <section class="card-section">
        <h2 data-testid="contributor-heading">Reviews where you're a contributor</h2>
        <p v-if="!contributorReviews.length" data-testid="contributor-empty" class="muted">
          You're not named on any quality event right now. You'll appear here as
          soon as a reviewer attaches an event to a review involving your work.
        </p>
        <div v-else class="review-list" data-testid="contributor-list">
          <ReviewRow
            v-for="r in contributorReviews"
            :key="r.id"
            :review="r"
            @open="onOpenReview"
          />
        </div>
      </section>

      <section class="card-section">
        <h2 data-testid="other-involved-heading">Other reviews involving you</h2>
        <p v-if="!otherInvolvedReviews.length" data-testid="other-involved-empty" class="muted">
          No reviews where you're the author, an event owner, or an event
          reporter (but not a contributor).
        </p>
        <div v-else class="review-list" data-testid="other-involved-list">
          <ReviewRow
            v-for="r in otherInvolvedReviews"
            :key="r.id"
            :review="r"
            @open="onOpenReview"
          />
        </div>
      </section>

      <section class="card-section">
        <h2 data-testid="standalone-heading">Standalone quality events involving you</h2>
        <p v-if="!involvedEvents.length" data-testid="standalone-empty" class="muted">
          No standalone quality events name you. Anyone can file one via the
          button above; it'll show up here as soon as you're the owner,
          reporter, or a named contributor.
        </p>
        <div v-else class="review-list" data-testid="standalone-list">
          <EventRow
            v-for="e in involvedEvents"
            :key="e.id"
            :event="e"
            @open="onOpenEvent"
          />
        </div>
      </section>
    </template>

    <!-- "Manager view" tab — full firehose, only rendered for managers. -->
    <template v-else-if="isManager && tab === 'manager'">
      <section class="card-section">
        <h2 data-testid="pending-board-heading">Reviews with pending QE responses</h2>
        <p class="muted board-intro">
          Approved reviews still waiting on a contributor's response, one column
          per dev. Padded to at least four columns with the busiest devs.
        </p>
        <div class="board-controls">
          <label for="pending-days-filter" class="board-filter-label">
            Pending for at least
          </label>
          <InputNumber
            v-model="pendingMinDays"
            input-id="pending-days-filter"
            :min="0"
            :max="365"
            show-buttons
            :step="1"
            suffix=" days"
            :input-style="{ width: '6.5rem' }"
            data-testid="pending-days-filter"
          />
          <span class="muted board-filter-hint">
            counted from the approval date · 0 shows every pending review
          </span>
        </div>
        <PendingResponseBoard :columns="pendingBoard" @open="onOpenReview" />
      </section>

      <section class="card-section">
        <h2 data-testid="all-reviews-heading">All reviews</h2>
        <p v-if="!allReviews.length" data-testid="all-reviews-empty" class="muted">
          Nothing in the ledger yet.
        </p>
        <div v-else class="review-list" data-testid="all-reviews-list">
          <ReviewRow
            v-for="r in allReviews"
            :key="r.id"
            :review="r"
            @open="onOpenReview"
          />
        </div>
      </section>

      <section class="card-section">
        <h2 data-testid="all-standalone-heading">All standalone quality events</h2>
        <p v-if="!allStandaloneEvents.length" data-testid="all-standalone-empty" class="muted">
          No standalone quality events in the ledger.
        </p>
        <div v-else class="review-list" data-testid="all-standalone-list">
          <EventRow
            v-for="e in allStandaloneEvents"
            :key="e.id"
            :event="e"
            @open="onOpenEvent"
          />
        </div>
      </section>
    </template>

    <ReviewDrawer
      :review="openReview"
      :visible="reviewDrawerVisible"
      :email="props.email"
      :is-manager="props.isManager"
      :load-error="deepLinkError"
      @update:visible="(v: boolean) => { if (!v) closeReviewDrawer(); }"
      @refresh="patchReview"
      @deleted="onReviewDeleted"
    />

    <Dialog
      v-model:visible="bulkConfirmVisible"
      modal
      header="Delete reviews"
      :style="{ width: 'min(480px, 92vw)' }"
      data-testid="bulk-delete-dialog"
    >
      <p>
        Are you sure you would like to delete
        <strong>{{ selectedIds.size }}</strong>
        review{{ selectedIds.size === 1 ? "" : "s" }} and their
        <strong>{{ selectedEventCount }}</strong>
        quality event{{ selectedEventCount === 1 ? "" : "s" }}? This cannot be
        undone.
      </p>
      <template #footer>
        <Button
          label="Cancel"
          severity="secondary"
          text
          :disabled="bulkDeleting"
          data-testid="bulk-delete-cancel"
          @click="bulkConfirmVisible = false"
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          :loading="bulkDeleting"
          data-testid="bulk-delete-confirm"
          @click="confirmBulkDelete"
        />
      </template>
    </Dialog>

    <EventDrawer
      :event="openEvent"
      :visible="eventDrawerVisible"
      :email="props.email"
      :is-manager="props.isManager"
      @update:visible="(v: boolean) => (eventDrawerVisible = v)"
      @refresh="refreshStandaloneEvents"
    />

    <EventCreateForm
      :visible="submitDrawerVisible"
      @update:visible="(v: boolean) => (submitDrawerVisible = v)"
      @created="refreshStandaloneEvents"
    />
  </template>
</template>

<style scoped>
.loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--p-surface-600);
}
.error {
  background: var(--p-red-50);
  color: var(--p-red-800);
  padding: 12px 16px;
  border-radius: 6px;
}
.page-intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.intro-text { flex: 1; min-width: 280px; }
.page-intro h1 {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
}
.page-intro .muted {
  font-size: 13px;
  color: var(--p-surface-600);
  max-width: 70ch;
  margin: 0;
}
.intro-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}
.refresh-group {
  display: flex;
  align-items: center;
  gap: 4px;
}
.refresh-stamp {
  font-size: 12px;
  color: var(--p-surface-500);
  white-space: nowrap;
}
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--p-surface-200);
  margin: 24px 0 16px;
}
.tab-btn {
  appearance: none;
  background: transparent;
  border: none;
  padding: 10px 16px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--p-surface-600);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 120ms, border-color 120ms;
}
.tab-btn:hover { color: var(--p-surface-900); }
.tab-btn.active {
  color: var(--p-primary-700);
  border-bottom-color: var(--p-primary-500);
}
.tab-btn:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
  border-radius: 3px;
}
.card-section { margin-bottom: 32px; }
.board-intro { margin: -4px 0 12px; font-size: 12px; }
.board-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 0 0 14px;
  padding: 10px 12px;
  background: var(--p-surface-50, #f8fafc);
  border: 1px solid var(--p-surface-200);
  border-radius: 6px;
}
.board-filter-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--p-surface-700);
}
.board-filter-hint { font-size: 12px; }
.card-section h2 {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--p-surface-500);
}
.muted { color: var(--p-surface-500); }
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.section-head h2 { margin: 0; }
.select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 4px 0 12px;
  padding: 8px 12px;
  background: var(--p-primary-50, #eff6ff);
  border: 1px solid var(--p-primary-200, #bfdbfe);
  border-radius: 6px;
}
.select-count { font-size: 13px; font-weight: 600; color: var(--p-surface-800); }
.review-list, .action-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.action-list li + li { margin-top: 6px; }
.action-item {
  display: flex;
  align-items: stretch;
  gap: 8px;
}
.action-tag {
  flex-shrink: 0;
  align-self: stretch;
  min-width: 84px;
  justify-content: center;
}
.action-target { flex: 1; min-width: 0; }
.quick-approve { flex-shrink: 0; align-self: center; }
/* Override the row component's sibling-margin rule — inside .action-item
   we use the parent list's li+li gap, not the per-row margin. */
.action-item :deep(.review-row),
.action-item :deep(.event-row) {
  margin-top: 0;
}
</style>
